import * as _ from 'underscore'

import { ConvertHelper } from './convert-helper'
import { Enums } from '../models/common/enums'

export type GetValueOptions = {
	defaultValue?: any
}

export class ConfigHelper {
	// Key/Section Values

	public static getSectionValue(section: Enums.SimaxConfigSection, options?: GetValueOptions): any
	public static getSectionValue<TValue>(section: Enums.SimaxConfigSection, options?: GetValueOptions): TValue
	public static getSectionValue(section: Enums.SimaxConfigSection, options?: GetValueOptions): any {
		if (section == null) {
			return undefined
		}

		const sectionConfig: any = this.getSectionConfig(section)

		if (sectionConfig === undefined && options && options.defaultValue !== undefined) {
			return options.defaultValue
		}

		return sectionConfig
	}

	public static getValue(section: Enums.SimaxConfigSection, key: string, options?: GetValueOptions): any
	public static getValue<TValue>(section: Enums.SimaxConfigSection, key: string, options?: GetValueOptions): TValue
	public static getValue(section: Enums.SimaxConfigSection, key: string, options?: GetValueOptions): any {
		if (section == null) {
			return undefined
		}

		const sectionConfig: any = this.getSectionConfig(section)
		let configValue: any = undefined

		if (sectionConfig == null) {
			if (options && options.defaultValue) {
				return options.defaultValue
			}

			return undefined
		}

		if (key != null) {
			key = key.toLowerCase()
		}

		if (Array.isArray(sectionConfig)) {
			switch (section) {
				case Enums.SimaxConfigSection.Languages:
					configValue = _.find(sectionConfig, (language: any) => language.locale.toLowerCase() === key)

					break

				case Enums.SimaxConfigSection.RtlState:
					configValue = _.find(sectionConfig, (state: any) => state.name.toLowerCase() === key)

					break

				case Enums.SimaxConfigSection.Themes:
					configValue = _.find(sectionConfig, (theme: any) => theme.name.toLowerCase() === key)

					break
			}
		} else {
			const configKey: string = _.findKey(sectionConfig, (configValue: string, configKey: string) => configKey.toLowerCase() === key)

			configValue = sectionConfig[configKey]
		}

		if (configValue === undefined) {
			if (options && options.defaultValue !== undefined) {
				return options.defaultValue
			}

			return this.getDefaultValue(section)
		}

		return configValue
	}

	public static getDefaultValue(section: Enums.SimaxConfigSection): any
	public static getDefaultValue<TValue>(section: Enums.SimaxConfigSection): TValue
	public static getDefaultValue(section: Enums.SimaxConfigSection): any {
		const sectionConfig: any = ConfigHelper.getSectionConfig(section)

		if (sectionConfig == null) {
			return undefined
		}

		switch (section) {
			case Enums.SimaxConfigSection.Icons:
			case Enums.SimaxConfigSection.Formats:
				return sectionConfig['DEFAULT']

			case Enums.SimaxConfigSection.Languages:
				const defaultLanguageLocale: string = ConfigHelper.getConfigValue('DEFAULT_LANGUAGE')

				return _.find(sectionConfig, (language: any) => language.locale === defaultLanguageLocale)

			case Enums.SimaxConfigSection.RtlState:
				const defaultRTLName: string = ConfigHelper.getConfigValue('DEFAULT_RTLSTATE')

				return _.find(sectionConfig, (state: any) => state.name === defaultRTLName)

			case Enums.SimaxConfigSection.Themes:
				const defaultThemeName: string = ConfigHelper.getConfigValue('DEFAULT_THEME')

				return _.find(sectionConfig, (theme: any) => theme.name === defaultThemeName)

			case Enums.SimaxConfigSection.Owner:
				return ConfigHelper.getConfigValue('OWNER')

			case Enums.SimaxConfigSection.LoginScreenTheme:
				return ConfigHelper.getConfigValue('LOGIN_SCREEN_THEME') || 'default'

			case Enums.SimaxConfigSection.Windows10CompatibileHidden:
				return ConfigHelper.getConfigValue('WINDOWS10_COMPATIBILE_HIDDEN') || false
		}

		return undefined
	}

	public static hasValue(section: Enums.SimaxConfigSection, key: string): boolean {
		return ConfigHelper.getValue(section, key) !== undefined
	}

	public static hasSectionValue(section: Enums.SimaxConfigSection): boolean {
		return ConfigHelper.getSectionConfig(section) !== undefined
	}

	// Key/Section Values

	// Custom Values

	public static getDateFormat(format: ConfigHelper.DateFormat): string {
		if (format == null) {
			throw new Error('Format cannot be undefined.')
		}

		return ConfigHelper.getGlobalValue('simax').getDateFormat(ConvertHelper.enumToString(ConfigHelper.DateFormat, format))
	}

	public static getDateTimeFormat(format: ConfigHelper.DateFormat, idDataType: number = null) {
		if (format == null) {
			throw new Error('Format cannot be undefined.')
		}

		return ConfigHelper.getGlobalValue('simax').getDateTimeFormat(ConvertHelper.enumToString(ConfigHelper.DateFormat, format), idDataType)
	}

	public static getIconName(icon: ConfigHelper.Icon): string {
		if (icon == null) {
			throw new Error('Icon cannot be undefined.')
		}

		return this.getValue(Enums.SimaxConfigSection.Icons, ConfigHelper.Icon[icon])
	}

	// Custom Values End

	// Private Methods

	/**
	 * Returns a value from the window object's config property at the given key.
	 */
	private static getConfigValue(key: string): any {
		const globalValue = ConfigHelper.getGlobalValue('config')

		return globalValue != null ? globalValue[key] : undefined
	}

	/**
	 * Returns a value from the window object at the given key.
	 */
	private static getGlobalValue(globalKey: string): any {
		return window[globalKey]
	}

	/**
	 * Retuns the whole config object for the given section.
	 */
	private static getSectionConfig(section: Enums.SimaxConfigSection): any {
		const sectionName: any = this.toUnderscore(Enums.SimaxConfigSection[section]).toUpperCase()

		return ConfigHelper.getConfigValue(sectionName)
	}

	// Private End

	// Utils

	private static toUnderscore(str: string): string {
		return str
			.split(/(?=[A-Z])/)
			.join('_')
			.toLowerCase()
	}

	// Utils End
}

export namespace ConfigHelper {
	export enum DateFormat {
		DATE_FORMAT_DX = 0,
		DATE_TIME_FORMAT_DX = 1,
		DATE_FORMAT_MOMENTJS = 2,
		DATE_TIME_FORMAT_MOMENTJS = 3,
	}

	export enum Icon {
		ACCORDION = 1,
		ACTIONS = 2,
		ADD = 3,
		ADD_LOCATION = 4,
		ALARM_DONE = 5,
		ALARM_GROUPS = 6,
		ALARMS = 7,
		ALARMS_ACTIVE = 8,
		ARROW_FORWARD = 9,
		ARROW_BACK = 10,
		ARROW_DROPDOWN = 11,
		ARROW_DROP_UP = 12,
		ATTACH_FILE = 13,
		CLOSE = 14,
		COLUMN_CHOOSER = 15,
		COLUMN_SETTINGS = 16,
		COMMON_DETAILS = 17,
		CONTACTS = 18,
		CONTENT_COPY = 19,
		DASHBOARD = 20,
		DASHBOARD_EDIT = 21,
		DATE_RANGE = 22,
		DEFAULT = 23,
		DELETE = 24,
		DETAILS = 25,
		DETAILS_POPUP_ACTIONS_BUTTON = 26,
		DETAILS_PREVIEW = 27,
		DEVICE_CONNECTIONS = 28,
		DEVICES = 29,
		DISTRIBUTORS = 30,
		DONE_ALL = 31,
		EDIT = 32,
		EXPORT = 33,
		FILTER = 34,
		FILTERS = 35,
		FIRST_BUTTON = 36,
		FORMAT_INCREASE = 37,
		FULLSCREEN = 38,
		HEADLINE = 39,
		HISTORY = 40,
		HOME = 41,
		IMPORT = 42,
		ISSUES = 43,
		LAST_BUTTON = 44,
		LEGEND = 45,
		LOCATIONS = 46,
		LOGOUT = 47,
		KB_ARROW_DOWN = 48,
		KB_ARROW_LEFT = 49,
		KPI = 50,
		MAP = 51,
		MEASUREMENTS = 52,
		METERS = 53,
		MULTISELECT = 54,
		NEXT = 55,
		NEXT_BUTTON = 56,
		NOTIFICATIONS = 57,
		PASSWORD = 58,
		PAUSE_BUTTON = 59,
		PLAY_BUTTON = 60,
		PREV = 61,
		PREV_BUTTON = 62,
		QUERY_BUILDER = 63,
		RECENT_ACTIONS = 64,
		REDO = 65,
		REFRESH = 66,
		REMOVE = 67,
		RENEW_BUTTON = 68,
		REORDER = 69,
		REPORT_GENERATE = 70,
		REPORTS = 71,
		REQUIRED_TAB = 72,
		ROTATE = 73,
		ROUTES = 74,
		RUN_ACTIONS = 75,
		SCHEMA = 76,
		SCHEDULE = 77,
		SCRIPTS = 78,
		SECURITY = 79,
		SELECT_ALL = 80,
		SELECTION = 81,
		SETTINGS = 82,
		SHOW_GRID = 83,
		SHOW_OPTIONS = 84,
		SIM_CARD = 85,
		SIZE = 86,
		STORAGE = 87,
		SWAP_HORIZ = 88,
		TASKS = 89,
		USER = 90,
		USER_OUTLINE = 91,
		USERS = 92,
		GROUP = 93,
		LOCATION_TYPE = 94,
		STAR = 95,
		STAR_BORDER = 96,
		LIST = 97,
		AUTO_RENEW = 98,
		DOLLAR = 99,
		SEND = 100,
		SWAP_VERT = 101,
		CONTACT_MAIL = 102,
		TARIFF = 103,
		ORDER = 104,
		PACKAGE = 105,
		CHECK = 106,
		UNDO = 107,
		AIRPORT_SHUTTLE = 108,
		DESCRIPTION = 109,
		ADD_CIRCLE_OUTLINE = 110,
		MY_LIBRARY_BOOKS = 111,
		DELETE_SWEEP = 112,
		VIEW_CAROUSEL = 113,
	}

	export class Key {
		public static get Aiut(): string {
			return 'AIUT'
		}
		public static get BackgroundColorChart(): string {
			return 'BACKGROUND_COLOR_CHART'
		}
		public static get Center(): string {
			return 'CENTER'
		}
		public static get Clesse(): string {
			return 'CLESSE'
		}
		public static get ClusterZoom(): string {
			return 'CLUSTER_ZOOM'
		}
		public static get DE_DE(): string {
			return 'de-DE'
		}
		public static get DefaultMarkerIcon(): string {
			return 'DEFAULT_MARKER_ICON'
		}
		public static get DefaultPageSize(): string {
			return 'DEFAULT_PAGE_SIZE'
		}
		public static get DetailsType(): string {
			return 'DETAILS_TYPE'
		}
		public static get DXDashboardUrl(): string {
			return 'DXDASHBOARD_URL'
		}
		public static get EN_GB(): string {
			return 'en-GB'
		}
		public static get EN_US(): string {
			return 'en-US'
		}
		public static get EN(): string {
			return 'EN'
		}
		public static get ES_ES(): string {
			return 'es-ES'
		}
		public static get ES(): string {
			return 'ES'
		}
		public static get FR_FR(): string {
			return 'fr-FR'
		}
		public static get IgnoredPointsOrderForRouteTypes(): string {
			return 'IGNORED_POINTS_ORDER_FOR_ROUTE_TYPES'
		}
		public static get InitDbCollectorProperties(): string {
			return 'INIT_DB_COLLECTOR_PROPERTIES'
		}
		public static get InitZoom(): string {
			return 'INIT_ZOOM'
		}
		public static get IsAggregationEnabled(): string {
			return 'IS_AGGREGATION_ENABLED'
		}
		public static get IsPieChartSeriesColoringEnabled(): string {
			return 'IS_PIECHART_SERIES_COLORING_ENABLED'
		}
		public static get MinBarHeight(): string {
			return 'MIN_BAR_HEIGHT'
		}
		public static get MinBarWidth(): string {
			return 'MIN_BAR_WIDTH'
		}
		public static get IT_IT(): string {
			return 'it-IT'
		}
		public static get Light(): string {
			return 'LIGHT'
		}
		public static get LTR(): string {
			return 'LTR'
		}
		public static get Map_Provider(): string {
			return 'MAP_PROVIDER'
		}
		public static get MapMaxZoom(): string {
			return 'MAP_MAX_ZOOM'
		}
		public static get MaxMapDeviceConnectionOKORange(): string {
			return 'MAX_MAP_DEVICE_CONNECTION_OKO_RANGE'
		}
		public static get MaxSchemaLimit(): string {
			return 'MAX_SCHEMA_LIMIT'
		}
		public static get MaxZoom(): string {
			return 'MAX_ZOOM'
		}
		public static get Mode(): string {
			return 'MODE'
		}
		public static get Orange(): string {
			return 'ORANGE'
		}
		public static get PageSizes(): string {
			return 'PAGE_SIZES'
		}
		public static get PL_PL(): string {
			return 'pl-PL'
		}
		public static get PL(): string {
			return 'PL'
		}
		public static get RESTUrl(): string {
			return 'REST_URL'
		}
		public static get RoutePointsPartSavingCount(): string {
			return 'ROUTE_POINTS_PART_SAVING_COUNT'
		}
		public static get RTL(): string {
			return 'RTL'
		}
		public static get RU_RU(): string {
			return 'ru-RU'
		}
		public static get SR_LATIN_RS(): string {
			return 'sr-Latn-RS'
		}
		public static get SR(): string {
			return 'SR'
		}
		public static get Url(): string {
			return 'URL'
		}
		public static get UseREST(): string {
			return 'USE_REST'
		}
		public static get PT_PT(): string {
			return 'pt-PT'
		}
	}
}
